import { cn } from "@/lib/utils";
import {
  Pagination as PaginationRoot,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@groot/ui/pagination";
import type { MouseEvent } from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  itemName?: string;
  className?: string;
}

type PageItem = { type: "page"; page: number } | { type: "ellipsis"; id: string };

/**
 * Page list with ellipses: always shows first/last page and a ±1 window
 * around the current page. Shows all pages when there are ≤ 7.
 */
function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => ({ type: "page" as const, page: i + 1 }));
  }

  const pages = [1, currentPage - 1, currentPage, currentPage + 1, totalPages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
  const uniquePages = [...new Set(pages)];

  const items: PageItem[] = [];
  let previousPage = 0;
  for (const page of uniquePages) {
    if (page - previousPage > 1) {
      items.push({ type: "ellipsis", id: `ellipsis-before-${page}` });
    }
    items.push({ type: "page", page });
    previousPage = page;
  }
  return items;
}

/**
 * High-level pagination bar: "Showing X to Y of Z items" + page controls.
 * Composed from the shadcn primitives in `@groot/ui/pagination`.
 */
export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  isLoading = false,
  itemName = "items",
  className,
}: PaginationProps) {
  if (totalItems === 0) return null;

  const pageCount = Math.max(1, totalPages);
  const page = Math.min(Math.max(1, currentPage), pageCount);
  const start = (page - 1) * itemsPerPage + 1;
  const end = Math.min(page * itemsPerPage, totalItems);
  const isFirstPage = page <= 1;
  const isLastPage = page >= pageCount;

  const handlePageClick = (event: MouseEvent<HTMLAnchorElement>, targetPage: number) => {
    event.preventDefault();
    if (isLoading || targetPage < 1 || targetPage > pageCount || targetPage === page) return;
    onPageChange(targetPage);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <p className="text-sm text-muted-foreground">
        Showing {start} to {end} of {totalItems} {itemName}
      </p>

      {pageCount > 1 && (
        <PaginationRoot className="mx-0 w-auto sm:justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                aria-disabled={isFirstPage || isLoading}
                className={cn((isFirstPage || isLoading) && "pointer-events-none opacity-50")}
                onClick={(event) => handlePageClick(event, page - 1)}
              />
            </PaginationItem>

            {getPageItems(page, pageCount).map((item) =>
              item.type === "ellipsis" ? (
                <PaginationItem key={item.id}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item.page}>
                  <PaginationLink
                    href="#"
                    isActive={item.page === page}
                    aria-disabled={isLoading}
                    className={cn(
                      item.page !== page && "hidden sm:inline-flex",
                      isLoading && "pointer-events-none opacity-50",
                    )}
                    onClick={(event) => handlePageClick(event, item.page)}
                  >
                    {item.page}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationNext
                href="#"
                aria-disabled={isLastPage || isLoading}
                className={cn((isLastPage || isLoading) && "pointer-events-none opacity-50")}
                onClick={(event) => handlePageClick(event, page + 1)}
              />
            </PaginationItem>
          </PaginationContent>
        </PaginationRoot>
      )}
    </div>
  );
}
